import { CheckCircle2, Circle } from 'lucide-react'
import { cn } from '~/shadcn/lib/utils'
import { useIsDesktop } from '~/@mid_ui/layout/useIsDesktop'
import type { Step } from './steps'
import type { OnboardingStepsColorScheme } from './useOnboardingSteps'

type OnboardingStepsProps = {
  steps: Step[]
  activeStep: number
  colorScheme?: OnboardingStepsColorScheme
  stepIconClassNameOverride?: string
  forceVertical?: boolean
}

export function OnboardingSteps({
  steps,
  activeStep,
  colorScheme = 'light',
  stepIconClassNameOverride,
  forceVertical = false,
}: OnboardingStepsProps) {
  const isDesktop = useIsDesktop()
  const isDark = colorScheme === 'dark'
  const stepWidth = 100 / steps.length
  const shouldUseVerticalLayout = forceVertical || !isDesktop

  return (
    <div
      className={cn(
        'w-full max-w-[780px] relative flex base:mx-auto',
        shouldUseVerticalLayout
          ? 'flex-col items-start gap-4 px-6'
          : 'flex-row items-start'
      )}>
      {/* Spojovacia čiara */}
      {steps.length > 1 && (
        <>
          {shouldUseVerticalLayout ? (
            <div
              className={cn(
                'absolute left-[calc(1rem+0.5rem+18px)] w-px z-0',
                isDark ? 'bg-gray-600' : 'bg-[#bbb]'
              )}
              style={{
                top: '11px',
                height: `calc(100% - ${(steps.length - 1) * 16}px)`,
              }}
            />
          ) : (
            <div
              className={cn(
                'absolute top-[19px] h-px z-0',
                isDark ? 'bg-gray-600' : 'bg-[#d2d2d2]'
              )}
              style={{
                left: `${stepWidth / 2}%`,
                right: `${stepWidth / 2}%`,
              }}
            />
          )}
        </>
      )}
      {steps.map((step, index) => {
        const isCompleted = index < activeStep
        const isActive = index === activeStep

        return (
          <div
            key={step.title}
            className={cn(
              'flex relative z-10 w-full items-center',
              shouldUseVerticalLayout ? 'flex-row gap-3' : 'flex-col'
            )}
            style={
              shouldUseVerticalLayout ? undefined : { width: `${stepWidth}%` }
            }>
            <div
              className={cn(
                'flex gap-0 items-center',
                shouldUseVerticalLayout ? 'flex-row gap-3' : 'flex-col'
              )}>
              <div
                className={cn(
                  'relative z-10 p-2 flex-shrink-0',

                  stepIconClassNameOverride
                    ? stepIconClassNameOverride
                    : isDark
                      ? 'bg-foreground'
                      : 'bg-[#FFF8EB]'
                )}>
                <StepIconComponent
                  active={isActive}
                  completed={isCompleted}
                  colorScheme={colorScheme}
                />
              </div>
              <div
                className={cn(
                  'flex flex-col',
                  shouldUseVerticalLayout ? 'items-start' : 'items-center'
                )}>
                <span
                  className={cn(
                    'font-semibold text-wrap',
                    shouldUseVerticalLayout ? 'text-left' : 'text-center',
                    isDark
                      ? isActive || isCompleted
                        ? 'text-accent'
                        : 'text-[#bbb]'
                      : isActive || isCompleted
                        ? 'text-primary'
                        : 'text-[#bbb]'
                  )}>
                  {step.title}
                </span>
                <p
                  className={cn(
                    'text-xs font-medium whitespace-nowrap',
                    shouldUseVerticalLayout ? 'text-left' : 'text-center',
                    isDark
                      ? isActive || isCompleted
                        ? 'text-accent'
                        : 'text-[#bbb]'
                      : isActive || isCompleted
                        ? 'text-primary'
                        : 'text-[#bbb]'
                  )}>
                  ({step.minutes} min)
                </p>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function StepIconComponent({
  active,
  completed,
  colorScheme = 'light',
}: {
  active: boolean
  completed: boolean
  colorScheme?: 'dark' | 'light'
}) {
  const isDark = colorScheme === 'dark'

  const iconClass = cn(
    'w-[22px] h-[22px]',
    isDark
      ? active || completed
        ? 'text-accent'
        : 'text-gray-400'
      : active || completed
        ? 'text-[#121212]'
        : 'text-[#bbb]'
  )

  if (completed) {
    return <CheckCircle2 className={iconClass} />
  }

  return <Circle className={iconClass} />
}
